import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Mail, 
  Lock, 
  Check, 
  AlertCircle, 
  Loader2, 
  ArrowRight, 
  Cloud, 
  ShieldCheck, 
  LogOut, 
  RefreshCw, 
  Eye, 
  EyeOff,
  UserCheck
} from 'lucide-react';
import { cn } from '../lib/utils';
import { 
  supabase, 
  isSupabaseConfigured, 
  signUpWithEmail, 
  signInWithEmail, 
  signInWithGoogle, 
  signOut, 
  resetPasswordForEmail,
  validatePassword,
  syncCloudStats
} from '../supabase';

interface AuthModalProps {
  onClose: () => void;
  isLightMode: boolean;
  user: any;
  onUserChange: (user: any) => void;
  getStats: () => any;
  onStatsUpdated: (stats: any) => void;
}

export function AuthModal({ 
  onClose, 
  isLightMode, 
  user, 
  onUserChange, 
  getStats, 
  onStatsUpdated 
}: AuthModalProps) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot' | 'profile'>(user ? 'profile' : 'signin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  
  // Cloudflare Turnstile token
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  const turnstileSiteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY;

  const passwordRules = validatePassword(password);

  // Initialize Turnstile if configured
  useEffect(() => {
    if (!turnstileSiteKey || mode === 'profile') return;

    const renderTurnstile = () => {
      if ((window as any).turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        try {
          const id = (window as any).turnstile.render(turnstileContainerRef.current, {
            sitekey: turnstileSiteKey,
            callback: (token: string) => {
              setCaptchaToken(token);
            },
            'expired-callback': () => {
              setCaptchaToken(undefined);
            },
            'error-callback': () => {
              setCaptchaToken(undefined);
            },
            theme: isLightMode ? 'light' : 'dark',
            size: 'flexible'
          });
          turnstileWidgetIdRef.current = id;
        } catch (e) {
          console.error("Turnstile render error:", e);
        }
      }
    };

    if (!(window as any).turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = renderTurnstile;
      document.head.appendChild(script);
    } else {
      renderTurnstile();
    }

    return () => {
      if (turnstileWidgetIdRef.current && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(turnstileWidgetIdRef.current);
        } catch {}
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [mode, turnstileSiteKey, isLightMode]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await signInWithEmail(email, password, captchaToken);
      if (data.user) {
        onUserChange(data.user);
        // Sync stats after login
        const local = getStats();
        const merged = await syncCloudStats(data.user, local);
        onStatsUpdated(merged);
        onClose();
      }
    } catch (err: any) {
      if (err.message?.includes('captcha_token')) {
        setError('Captcha protection is enabled in your Supabase dashboard, but VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is missing in your environment variables. Add it, or disable Captcha in Supabase -> Authentication.');
      } else {
        setError(err.message || 'Failed to sign in. Please check your credentials.');
      }
      // Reset turnstile if needed
      if (turnstileWidgetIdRef.current && (window as any).turnstile) {
        (window as any).turnstile.reset(turnstileWidgetIdRef.current);
        setCaptchaToken(undefined);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!passwordRules.isValid) {
      setError('Please meet all password requirements before signing up.');
      return;
    }

    setLoading(true);

    try {
      const data = await signUpWithEmail(email, password, username, captchaToken);
      if (data.user && !data.session) {
        // Confirmation email was sent
        setVerificationEmailSent(true);
      } else if (data.user && data.session) {
        // Auto-confirmed or existing session
        onUserChange(data.user);
        const local = getStats();
        const merged = await syncCloudStats(data.user, local);
        onStatsUpdated(merged);
        onClose();
      }
    } catch (err: any) {
      if (err.message?.includes('captcha_token')) {
        setError('Captcha protection is enabled in your Supabase dashboard, but VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is missing in your environment variables. Add it, or disable Captcha in Supabase -> Authentication.');
      } else {
        setError(err.message || 'Failed to create account.');
      }
      if (turnstileWidgetIdRef.current && (window as any).turnstile) {
        (window as any).turnstile.reset(turnstileWidgetIdRef.current);
        setCaptchaToken(undefined);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      await resetPasswordForEmail(email, captchaToken);
      setSuccessMessage('Password reset link sent! Please check your inbox.');
    } catch (err: any) {
      if (err.message?.includes('captcha_token')) {
        setError('Captcha protection is enabled in your Supabase dashboard, but VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is missing in your environment variables. Add it, or disable Captcha in Supabase -> Authentication.');
      } else {
        setError(err.message || 'Failed to send password reset email.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google.');
    }
  };

  const handleManualSync = async () => {
    if (!user) return;
    setSyncing(true);
    try {
      const local = getStats();
      const merged = await syncCloudStats(user, local);
      onStatsUpdated(merged);
      setSuccessMessage('Stats successfully synced to your account!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError('Failed to sync stats.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
      onUserChange(null);
      onClose();
    } catch (err: any) {
      setError('Failed to sign out.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetUsername = async () => {
    if (!username.trim()) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (supabase) {
        const { data, error } = await supabase.auth.updateUser({
          data: { 
            username: username.trim(),
            name: username.trim(),
            full_name: username.trim(),
            display_name: username.trim()
          }
        });
        if (error) throw error;
        if (data.user) {
          onUserChange(data.user);
        }
        setSuccessMessage('Username updated successfully!');
        setUsername('');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update username.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className={cn(
          "relative w-full max-w-md rounded-3xl p-6 md:p-8 shadow-2xl border transition-colors max-h-[90vh] overflow-y-auto z-10",
          isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#222222] border-white/10"
        )}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className={cn(
            "absolute top-5 right-5 p-2 rounded-full transition-colors",
            isLightMode ? "hover:bg-black/5 text-gray-500" : "hover:bg-white/5 text-gray-400"
          )}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Verification Email Sent Screen */}
        {verificationEmailSent ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
              <Mail className="w-8 h-8" />
            </div>
            <h2 className={cn("text-2xl font-bold mb-2", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
              Verify your email
            </h2>
            <p className={cn("text-sm mb-6 leading-relaxed", isLightMode ? "text-gray-600" : "text-gray-300")}>
              We sent a verification link to <span className="font-bold text-[#ff0080]">{email}</span>. Please check your inbox and tap the link to activate your SongToSong account.
            </p>
            <div className={cn(
              "p-4 rounded-2xl text-xs mb-6 text-left border",
              isLightMode ? "bg-black/5 border-black/5 text-gray-600" : "bg-white/5 border-white/5 text-gray-400"
            )}>
              <p className="font-bold mb-1">Didn't receive an email?</p>
              <p>Check your spam folder, or try logging in once you've clicked the link.</p>
            </div>
            <button
              onClick={() => {
                setVerificationEmailSent(false);
                setMode('signin');
              }}
              className="w-full py-4 rounded-full font-bold bg-[#ff0080] text-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-[#ff0080]/20"
            >
              Back to Sign In
            </button>
          </div>
        ) : mode === 'profile' && user ? (
          /* Profile / Sync View */
          <div>
            {!user.user_metadata?.username ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-[#ff0080]/10 border border-[#ff0080]/20 rounded-full flex items-center justify-center mx-auto mb-4 text-[#ff0080]">
                  <UserCheck className="w-8 h-8" />
                </div>
                <h2 className={cn("text-xl font-bold mb-2", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                  Set Your Username
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  You signed in with Google, but you need a username before you can continue.
                </p>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-xs font-bold flex items-center gap-2 text-left">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="relative mb-6">
                  <UserCheck className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="coolplayer123"
                    className={cn(
                      "w-full pl-12 pr-4 py-4 rounded-2xl border text-sm outline-none transition-all text-left",
                      isLightMode 
                        ? "bg-white border-black/10 focus:border-[#ff0080] text-[#1a1a1a]" 
                        : "bg-black/20 border-white/10 focus:border-[#ff0080] text-white"
                    )}
                  />
                </div>
                
                <button
                  onClick={handleSetUsername}
                  disabled={loading || !username.trim()}
                  className="w-full py-4 rounded-full font-bold bg-[#ff0080] text-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-[#ff0080]/20 disabled:opacity-50 mb-3"
                >
                  {loading ? 'Saving...' : 'Save Username'}
                </button>
                
                <button
                  onClick={handleSignOut}
                  disabled={loading}
                  className="text-xs font-bold text-gray-400 hover:text-red-500 transition-colors"
                >
                  Sign Out / Switch Account
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8 mt-2">
                  <h2 className={cn("text-2xl font-black truncate", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                    {user.user_metadata?.username}
                  </h2>
                </div>

                {successMessage && (
                  <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-500 text-xs font-bold flex items-center gap-2">
                    <Check className="w-4 h-4 shrink-0" />
                    <span>{successMessage}</span>
                  </div>
                )}

                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-xs font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Quick Stats Summary */}
                <div className={cn(
                  "grid grid-cols-2 gap-3 p-4 rounded-2xl border mb-6",
                  isLightMode ? "bg-black/5 border-black/5" : "bg-white/5 border-white/5"
                )}>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-500">Current Streak</p>
                    <p className="text-xl font-bold text-[#ff0080]">{getStats().streak} days 🔥</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-500">Daily Wins</p>
                    <p className={cn("text-xl font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                      {getStats().won} / {getStats().played}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleSignOut}
                    disabled={loading}
                    className="w-full py-3.5 rounded-full font-bold text-sm text-red-500 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {loading ? 'Signing out...' : 'Sign Out'}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          /* Sign In / Sign Up Form */
          <div>
            <div className="mb-6">
              <h2 className={cn("text-2xl font-bold mb-1", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                {mode === 'signup' ? 'Create an Account' : mode === 'forgot' ? 'Reset Password' : 'Sign In'}
              </h2>
              <p className={cn("text-sm", isLightMode ? "text-gray-600" : "text-gray-400")}>
                {mode === 'signup' 
                  ? 'Keep your daily streaks and game history synced across all devices.' 
                  : mode === 'forgot'
                  ? 'Enter your email and we will send you a reset link.'
                  : 'Welcome back! Sign in to sync your progress.'}
              </p>
            </div>

            {!isSupabaseConfigured && (
              <div className="mb-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs leading-relaxed">
                <p className="font-bold mb-1">Supabase Setup Notice</p>
                Please provide your <code className="bg-amber-500/20 px-1 py-0.5 rounded">VITE_SUPABASE_URL</code> and <code className="bg-amber-500/20 px-1 py-0.5 rounded">VITE_SUPABASE_ANON_KEY</code> in environment variables to enable authentication.
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-xs font-bold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-500 text-xs font-bold flex items-center gap-2">
                <Check className="w-4 h-4 shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Google Sign In Option */}
            {mode !== 'forgot' && (
              <>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={!isSupabaseConfigured || loading}
                  className={cn(
                    "w-full py-3.5 px-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-3 border transition-all mb-4",
                    isLightMode 
                      ? "bg-white border-black/10 text-[#1a1a1a] hover:bg-gray-50 shadow-sm" 
                      : "bg-white/5 border-white/10 text-white hover:bg-white/10"
                  )}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </button>

                <div className="flex items-center gap-3 my-5">
                  <div className={cn("flex-1 h-px", isLightMode ? "bg-black/10" : "bg-white/10")} />
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">or with email</span>
                  <div className={cn("flex-1 h-px", isLightMode ? "bg-black/10" : "bg-white/10")} />
                </div>
              </>
            )}

            <form onSubmit={mode === 'signup' ? handleSignUp : mode === 'forgot' ? handleForgotPassword : handleSignIn} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className={cn("block text-xs font-bold mb-1.5", isLightMode ? "text-gray-700" : "text-gray-300")}>
                    Username
                  </label>
                  <div className="relative">
                    <UserCheck className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      required={mode === 'signup'}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="coolplayer123"
                      className={cn(
                        "w-full pl-11 pr-4 py-3.5 rounded-2xl border text-sm outline-none transition-all",
                        isLightMode 
                          ? "bg-white border-black/10 focus:border-[#ff0080] text-[#1a1a1a]" 
                          : "bg-white/5 border-white/10 focus:border-[#ff0080] text-white"
                      )}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className={cn("block text-xs font-bold mb-1.5", isLightMode ? "text-gray-700" : "text-gray-300")}>
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={cn(
                      "w-full pl-11 pr-4 py-3.5 rounded-2xl border text-sm outline-none transition-all",
                      isLightMode 
                        ? "bg-white border-black/10 focus:border-[#ff0080] text-[#1a1a1a]" 
                        : "bg-white/5 border-white/10 focus:border-[#ff0080] text-white"
                    )}
                  />
                </div>
              </div>

              {mode !== 'forgot' && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={cn("block text-xs font-bold", isLightMode ? "text-gray-700" : "text-gray-300")}>
                      Password
                    </label>
                    {mode === 'signin' && (
                      <button
                        type="button"
                        onClick={() => {
                          setMode('forgot');
                          setError(null);
                          setSuccessMessage(null);
                        }}
                        className="text-xs font-bold text-[#ff0080] hover:underline"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={cn(
                        "w-full pl-11 pr-11 py-3.5 rounded-2xl border text-sm outline-none transition-all",
                        isLightMode 
                          ? "bg-white border-black/10 focus:border-[#ff0080] text-[#1a1a1a]" 
                          : "bg-white/5 border-white/10 focus:border-[#ff0080] text-white"
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Password Requirements Checklist (for Sign Up) */}
              {mode === 'signup' && (
                <div className={cn(
                  "p-3.5 rounded-2xl border text-xs space-y-2",
                  isLightMode ? "bg-black/5 border-black/5" : "bg-white/5 border-white/5"
                )}>
                  <p className="font-bold text-gray-500 text-[10px] uppercase tracking-wider">Password Requirements</p>
                  
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center text-[10px] transition-colors",
                      passwordRules.hasMinLength ? "bg-emerald-500 text-white" : "bg-gray-400/20 text-gray-500"
                    )}>
                      {passwordRules.hasMinLength ? <Check className="w-2.5 h-2.5 stroke-[3]" /> : "•"}
                    </div>
                    <span className={cn(passwordRules.hasMinLength ? "text-emerald-500 font-semibold" : "text-gray-500")}>
                      At least 8 characters
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center text-[10px] transition-colors",
                      passwordRules.hasUppercase ? "bg-emerald-500 text-white" : "bg-gray-400/20 text-gray-500"
                    )}>
                      {passwordRules.hasUppercase ? <Check className="w-2.5 h-2.5 stroke-[3]" /> : "•"}
                    </div>
                    <span className={cn(passwordRules.hasUppercase ? "text-emerald-500 font-semibold" : "text-gray-500")}>
                      At least 1 capital letter (A-Z)
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center text-[10px] transition-colors",
                      passwordRules.hasNumber ? "bg-emerald-500 text-white" : "bg-gray-400/20 text-gray-500"
                    )}>
                      {passwordRules.hasNumber ? <Check className="w-2.5 h-2.5 stroke-[3]" /> : "•"}
                    </div>
                    <span className={cn(passwordRules.hasNumber ? "text-emerald-500 font-semibold" : "text-gray-500")}>
                      At least 1 number (0-9)
                    </span>
                  </div>
                </div>
              )}

              {/* Cloudflare Turnstile Widget Placeholder */}
              {turnstileSiteKey && (
                <div ref={turnstileContainerRef} className="my-3 min-h-[65px] flex justify-center" />
              )}

              <button
                type="submit"
                disabled={loading || !isSupabaseConfigured || (mode === 'signup' && !passwordRules.isValid)}
                className={cn(
                  "w-full py-4 rounded-full font-bold text-base flex items-center justify-center gap-2 transition-all shadow-lg mt-2",
                  "bg-[#ff0080] text-white shadow-[#ff0080]/20 hover:scale-[1.02] active:scale-[0.98]",
                  (loading || !isSupabaseConfigured || (mode === 'signup' && !passwordRules.isValid)) && "opacity-60 cursor-not-allowed hover:scale-100"
                )}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>{mode === 'signup' ? 'Create Account' : mode === 'forgot' ? 'Send Reset Link' : 'Sign In'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

                <div className="mt-6 text-center text-xs">
                  {mode === 'signin' ? (
                    <p className="text-gray-500">
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setMode('signup');
                          setError(null);
                          setSuccessMessage(null);
                        }}
                        className="font-bold text-[#ff0080] hover:underline"
                      >
                        Sign up now
                      </button>
                    </p>
                  ) : (
                    <p className="text-gray-500">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setMode('signin');
                          setError(null);
                          setSuccessMessage(null);
                        }}
                        className="font-bold text-[#ff0080] hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  )}
                </div>

                {mode === 'signup' && (
                  <p className="mt-4 text-[11px] text-gray-500 text-center leading-relaxed">
                    By signing up, you agree to our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#ff0080] hover:underline font-medium">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#ff0080] hover:underline font-medium">Privacy Policy</a>.
                  </p>
                )}
              </div>
        )}
      </motion.div>
    </div>
  );
}
